/**
 * AK0 requisition helpers shared by AK0 Supply and Cannibalization views.
 *
 * Single Hit = aircraft has exactly one open AK0 requisition line.
 * Requisitions are identified by JCN + MCN (work order / maintenance control),
 * not by part number — the same part on separate JCN/MCN counts as multiple hits.
 */

export function ak0RequisitionKey(row) {
    const jcn = (row.jcn || '').toString().trim();
    const mcn = (row.mcn || '').toString().trim();
    if (jcn || mcn) {
        return `${jcn}::${mcn}`;
    }
    const rqnNo = (row.rqn_no || '').toString().trim();
    if (rqnNo) {
        return `rqn::${rqnNo}`;
    }
    if (row.id != null) {
        return `id::${row.id}`;
    }
    return `row::${(row.part_no || '').toString().trim()}::${(row.est_delv_dt || '').toString()}::${(row.ord_dt || '').toString()}`;
}

/** @returns {Map<string, { requisitions: Map<string, string>, latestEdd: Date|null }>} */
export function buildAircraftAk0RequisitionMap(ak0Rows) {
    const map = new Map();

    for (const row of ak0Rows || []) {
        const partNo = (row.part_no || '').toString().trim();
        if (!partNo) continue;

        const modex = (row.modex || '').toString().trim() || '__UNKNOWN__';
        const site = (row.site_name || '').toString().trim() || '__UNKNOWN_SITE__';
        const aircraftKey = `${site}::${modex}`;

        let agg = map.get(aircraftKey);
        if (!agg) {
            agg = { requisitions: new Map(), latestEdd: null };
            map.set(aircraftKey, agg);
        }

        agg.requisitions.set(ak0RequisitionKey(row), partNo);

        const eddStr = row.est_delv_dt;
        if (eddStr) {
            const str = String(eddStr).slice(0, 10);
            const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
            let edd = null;
            if (m) {
                edd = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
            } else {
                const d = new Date(str);
                if (!isNaN(d.getTime())) {
                    edd = new Date(d.getFullYear(), d.getMonth(), d.getDate());
                }
            }
            if (edd && (!agg.latestEdd || edd > agg.latestEdd)) {
                agg.latestEdd = edd;
            }
        }
    }

    return map;
}

export function isAircraftSingleHitAgg(agg) {
    return !!agg && agg.requisitions.size === 1;
}

export function isAircraftSingleHitForPartAgg(agg, partNo) {
    if (!isAircraftSingleHitAgg(agg)) return false;
    const onlyPart = [...agg.requisitions.values()][0];
    return onlyPart === partNo;
}

export function isAk0Row(row) {
    return (row.proj || '').toString().trim().toUpperCase() === 'AK0';
}

export function filterAk0Rows(rawData) {
    return (rawData || []).filter(isAk0Row);
}
